'use client'

import React, { useState } from 'react'
import { ChevronDown, ChevronUp } from 'lucide-react'

const C = { PRIMARY: '#F8F246', SECONDARY: '#2CBBC3', BROWN: '#8C5826', BORDER: '#ffe0c2', TEXT: '#1a1a1a', MUTED: '#6b7280' }

const LIMIT = 6

interface FAQ { question: string; answer: string }

const FAQAccordion = ({ faqs }: { faqs: FAQ[] }) => {
    const [open, setOpen] = useState<number | null>(0)
    const [showAll, setShowAll] = useState(false)

    const hasMore = faqs.length > LIMIT
    const visibleFaqs = showAll ? faqs : faqs.slice(0, LIMIT)

    return (
        <div>
            <div className="space-y-3">
                {visibleFaqs.map((faq, idx) => {
                    const isOpen = open === idx
                    return (
                        <div
                            key={idx}
                            className="rounded-2xl overflow-hidden"
                            style={{
                                background: '#ffffff',
                                border: `1px solid ${C.BORDER}`,
                                borderLeft: isOpen ? `3px solid ${C.PRIMARY}` : `3px solid transparent`,
                                boxShadow: isOpen ? '0 6px 24px rgba(255,153,52,0.1)' : '0 1px 4px rgba(0,0,0,0.04)',
                            }}
                        >
                            <button
                                onClick={() => setOpen(isOpen ? null : idx)}
                                className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left bg-transparent border-none cursor-pointer"
                                aria-expanded={isOpen}
                            >
                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                    <span
                                        className="flex-shrink-0 w-6 h-6 rounded-lg flex items-center justify-center text-[10px] font-black"
                                        style={{
                                            background: isOpen ? C.PRIMARY : `${C.PRIMARY}10`,
                                            color: isOpen ? '#8C5826' : C.BROWN,
                                        }}
                                    >
                                        {idx + 1}
                                    </span>
                                    <span
                                        className="text-sm font-semibold leading-snug"
                                        style={{ color: isOpen ? C.TEXT : C.MUTED }}
                                    >
                                        {faq.question}
                                    </span>
                                </div>
                                <ChevronDown
                                    size={16}
                                    style={{
                                        color: isOpen ? C.BROWN : C.MUTED,
                                        transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                        transition: 'transform 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                                        flexShrink: 0,
                                    }}
                                />
                            </button>
                            <div
                                className="grid transition-[grid-template-rows] duration-300 ease-in-out overflow-hidden"
                                style={{ gridTemplateRows: isOpen ? '1fr' : '0fr' }}
                            >
                                <div className="overflow-hidden">
                                    <div
                                        className="px-5 pb-5 sm:pl-14 text-sm leading-relaxed"
                                        style={{
                                            color: C.MUTED,
                                            borderTop: `1px solid ${C.BORDER}`,
                                            paddingTop: '0.75rem',
                                        }}
                                        dangerouslySetInnerHTML={{ __html: faq.answer }}
                                    />
                                </div>
                            </div>
                        </div>
                    )
                })}
            </div>

            {hasMore && (
                <div className="flex justify-center mt-8">
                    <button
                        onClick={() => {
                            setShowAll(prev => !prev)
                            // Collapse open accordion when hiding
                            if (showAll && open !== null && open >= LIMIT) setOpen(null)
                        }}
                        className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl text-sm font-bold transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg active:scale-95"
                        style={{
                            background: `linear-gradient(135deg, ${C.PRIMARY} 0%, #ffb347 100%)`,
                            color: C.BROWN,
                            boxShadow: '0 4px 14px rgba(140, 88, 38, 0.2)',
                            border: `1px solid ${C.PRIMARY}`,
                        }}
                    >
                        {showAll ? (
                            <>
                                <ChevronUp size={16} />
                                Show Less
                            </>
                        ) : (
                            <>
                                <ChevronDown size={16} />
                                Show More
                            </>
                        )}
                    </button>
                </div>
            )}
        </div>
    )
}

export default FAQAccordion